import type { PlanContent } from "@szyos/contracts";
import type { ApprovalKind } from "@szyos/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { api, errorMessage } from "../api";
import { useToast } from "./feedback";
import { Icon } from "../icons";

export interface ApprovalEntry {
  workOrderId: string;
  title: string;
  projectTitle: string;
  kind: ApprovalKind;
  version: number;
  specHash: string | null;
  planHash: string | null;
  proposal: PlanContent | null;
  points: string[];
}

const ACTION: Record<ApprovalKind, string> = {
  spec: "Briefing freigeben",
  plan: "Plan freigeben und starten",
  start: "Starten",
};

/** Führt genau die Schritte aus, die der Owner sonst einzeln auf der Auftragsseite tippt. */
async function approve(e: ApprovalEntry): Promise<void> {
  if (e.kind === "spec") {
    await api.approveSpec(e.workOrderId, e.specHash ?? "", e.version);
    return;
  }
  let version = e.version;
  if (e.kind === "plan") {
    let planHash = e.planHash;
    if (e.proposal) {
      const submitted = await api.submitPlan(e.workOrderId, e.proposal, version);
      planHash = submitted.planHash;
      version = submitted.version;
    }
    version = (await api.approvePlan(e.workOrderId, planHash ?? "", version)).version;
  }
  await api.start(e.workOrderId, version);
}

/**
 * Sammelfreigabe (UX-10): alles, was auf deine Freigabe oder deinen Start wartet, an einer Stelle. Jede Freigabe
 * gilt für genau den angezeigten Stand; Reviews, Entscheidungen und Releases bleiben einzeln.
 */
export function ApprovalQueue() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const list = useQuery({ queryKey: ["approvals"], queryFn: api.approvals });
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<string | null>(null);
  const entries = list.data ?? [];
  const selected = entries.filter((e) => chosen.has(e.workOrderId));

  const run = useMutation({
    mutationFn: async (batch: ApprovalEntry[]) => {
      const failed: string[] = [];
      for (const [i, e] of batch.entries()) {
        setProgress(`${i + 1} von ${batch.length}: ${e.projectTitle}`);
        try {
          await approve(e);
        } catch (err) {
          failed.push(`${e.projectTitle}: ${errorMessage(err)}`);
        }
      }
      return { done: batch.length - failed.length, failed };
    },
    onSettled: async () => {
      setProgress(null);
      setChosen(new Set());
      await Promise.all(
        ["approvals", "work-orders", "work-order", "studio"].map((k) =>
          queryClient.invalidateQueries({ queryKey: [k] }),
        ),
      );
    },
    onSuccess: ({ done, failed }) => {
      if (failed.length === 0) toast(`${done} ${done === 1 ? "Freigabe" : "Freigaben"} erledigt.`);
      else toast(`${done} erledigt, ${failed.length} nicht: ${failed.join("; ")}`, "error");
    },
  });

  if (list.isLoading || entries.length === 0) return null;
  const toggle = (id: string) => {
    const next = new Set(chosen);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setChosen(next);
  };
  return (
    <section className="panel approval-queue" aria-labelledby="approvalHead">
      <div className="panel-head">
        <h2 id="approvalHead">Wartet auf deine Freigabe ({entries.length})</h2>
      </div>
      <p className="muted">
        Wähle aus, was weiterlaufen soll. Jede Freigabe gilt genau für den angezeigten Stand. Ein
        freigegebener Plan startet sofort.
      </p>
      <div className="idea-actions">
        <button
          type="button"
          className="btn"
          disabled={run.isPending}
          onClick={() =>
            setChosen(chosen.size === entries.length ? new Set() : new Set(entries.map((e) => e.workOrderId)))
          }
        >
          <span className="btn-label">
            {chosen.size === entries.length ? "Keine auswählen" : "Alle auswählen"}
          </span>
        </button>
        <button
          type="button"
          className="btn btn--primary"
          disabled={run.isPending || selected.length === 0}
          onClick={() => run.mutate(selected)}
        >
          <span className="btn-label">
            <Icon name="check" /> Ausgewählte freigeben ({selected.length})
          </span>
        </button>
      </div>
      {progress && (
        <p className="muted" role="status">
          <span className="pulse-dot" aria-hidden="true" /> {progress}
        </p>
      )}
      <ul className="approval-list">
        {entries.map((e) => (
          <li key={e.workOrderId}>
            <label className="check-row approval-pick">
              <input
                type="checkbox"
                checked={chosen.has(e.workOrderId)}
                onChange={() => toggle(e.workOrderId)}
                disabled={run.isPending}
              />{" "}
              <span>
                <strong>{e.projectTitle}</strong> · {e.title}
                <small className="approval-kind">{ACTION[e.kind]}</small>
              </span>
            </label>
            {e.points.length > 0 && (
              <details className="approval-points">
                <summary>
                  {e.kind === "spec" ? `${e.points.length} Muss-Funktionen` : `${e.points.length} Aufgaben`}{" "}
                  ansehen
                </summary>
                <ul>
                  {e.points.map((p, i) => (
                    <li key={`${e.workOrderId}-${i}`}>{p}</li>
                  ))}
                </ul>
              </details>
            )}
            <Link to="/work-orders/$orderId" params={{ orderId: e.workOrderId }} className="link small">
              Auftrag öffnen <Icon name="arrow" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
